import React, { useState } from "react";
import styled from "styled-components";

import SecureScreenContainer from "../components/common/containers/ScreenContainer";
import Header from "../components/common/Header";
import Button from "../components/common/Button";
import Modal from "../components/common/Modal";
import TransactionAddForm from "../components/forms/TransactionAddForm";
import { useTransactions } from "../hooks/useTransactions";
import { theme } from "../defs/theme";
import formatMoney from "../utils/formatMoney";
import TrashIcon from "../resources/images/trash.svg";
import PlusIcon from "../resources/images/plusIcon.svg";

const TransactionsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const HeaderSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
`;

const ActionButtons = styled.div`
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
`;

const TransactionsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const TransactionCard = styled.div`
    background: ${theme.palette.background.main};
    border-radius: 8px;
    padding: 20px;
    ${theme.shadow.med};
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
`;

const TransactionInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    min-width: 200px;
`;

const TransactionPayee = styled.div`
    font-size: ${theme.font.size.big};
    font-weight: ${theme.font.weight.bold};
    color: ${theme.palette.background.contrast};
`;

const TransactionDetails = styled.div`
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    font-size: ${theme.font.size.small};
    color: ${theme.palette.background.contrast};
    opacity: 0.7;
`;

const TransactionAmount = styled.div<{ negative?: boolean }>`
    font-size: ${theme.font.size.bigger};
    font-weight: ${theme.font.weight.bold};
    color: ${(props) => (props.negative ? theme.palette.error.main : theme.palette.primary.main)};
    text-align: right;
`;

const TransactionActions = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
`;

const DeleteButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: background-color 0.2s;

    &:hover {
        background-color: ${theme.palette.error.light};
    }

    &:active {
        background-color: ${theme.palette.error.main};
    }

    svg {
        width: 20px;
        height: 20px;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 48px 24px;
    color: ${theme.palette.background.contrast};
    opacity: 0.6;
    font-size: ${theme.font.size.big};
`;

const ErrorMessage = styled.div`
    background: ${theme.palette.error.light};
    color: ${theme.palette.error.main};
    padding: 12px;
    border-radius: 8px;
    font-size: ${theme.font.size.small};
`;

const LoadingMessage = styled.div`
    text-align: center;
    padding: 24px;
    color: ${theme.palette.background.contrast};
    opacity: 0.6;
`;

const formatDate = (date: Date): string => {
    return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
};

export default function TransactionsScreen(): JSX.Element {
    const { transactions, isLoading, error, deleteTransaction } = useTransactions();
    const [showAddModal, setShowAddModal] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const handleDelete = async (transactionId: string) => {
        if (window.confirm("Are you sure you want to delete this transaction?")) {
            setDeletingId(transactionId);
            try {
                await deleteTransaction(transactionId);
            } finally {
                setDeletingId(null);
            }
        }
    };

    const handleAddSuccess = () => {
        setShowAddModal(false);
    };

    const formatTransactionAmount = (activity: number): string => {
        // activity is in cents, convert to dollars
        const dollars = activity / 100;
        const formatted = formatMoney(Math.abs(dollars), 2);
        return dollars < 0 ? `-$${formatted}` : `$${formatted}`;
    };

    return (
        <SecureScreenContainer>
            <TransactionsContainer>
                <HeaderSection>
                    <Header>Transactions</Header>
                    <ActionButtons>
                        <Button icon={<PlusIcon />} onClick={() => setShowAddModal(true)}>
                            Add Transaction
                        </Button>
                    </ActionButtons>
                </HeaderSection>

                {error && <ErrorMessage>{error}</ErrorMessage>}

                {isLoading ? (
                    <LoadingMessage>Loading transactions...</LoadingMessage>
                ) : transactions.length === 0 ? (
                    <EmptyState>No transactions yet. Add a transaction to get started!</EmptyState>
                ) : (
                    <TransactionsList>
                        {transactions.map((transaction) => (
                            <TransactionCard key={transaction.id}>
                                <TransactionInfo>
                                    <TransactionPayee>{transaction.payee}</TransactionPayee>
                                    <TransactionDetails>
                                        {transaction.category && <span>{transaction.category}</span>}
                                        <span>{transaction.account}</span>
                                        <span>{formatDate(transaction.date)}</span>
                                        {transaction.note && <span>{transaction.note}</span>}
                                    </TransactionDetails>
                                </TransactionInfo>
                                <TransactionAmount negative={transaction.activity < 0}>
                                    {formatTransactionAmount(transaction.activity)}
                                </TransactionAmount>
                                <TransactionActions>
                                    {transaction.id && (
                                        <DeleteButton
                                            onClick={() => handleDelete(transaction.id!)}
                                            disabled={deletingId === transaction.id}
                                            title="Delete transaction"
                                        >
                                            <TrashIcon />
                                        </DeleteButton>
                                    )}
                                </TransactionActions>
                            </TransactionCard>
                        ))}
                    </TransactionsList>
                )}

                <Modal visible={showAddModal} onClose={() => setShowAddModal(false)}>
                    <TransactionAddForm onSubmit={handleAddSuccess} />
                </Modal>
            </TransactionsContainer>
        </SecureScreenContainer>
    );
}
